
import { triggerConfig, TriggerConfig, fetchPublicEmailTemplateByOrganizationId } from "./emailConfig";
import logo from "../assets/images/Black_Version.png";
import { getCloudFileById } from "components/Imageshelper";
import { getAuthCookie, resolveEmailTemplateBrandingOrganizationId, resolveEmailTemplateBrandingUserId } from "./authCookies";

/** simple {{key}} replacement */
function interpolate(str: string, params: Record<string,string>) {
  return Object.entries(params)
    .reduce((s, [k,v]) => s.replaceAll(`{{${k}}}`, v), str);
}

/**
 * Triggers whose `triggerConfig` CTA `url` is kept as-is (e.g. {{viewPayUrl}}, {{reviewUrl}}).
 * All other triggers use {@link emailCtaLoginUrl} for the primary CTA button only.
 */
const TRIGGER_CTA_KEEP_CONFIGURED_URL = new Set([
  "salesorder_sendquote",
  "installationjobs_sendquote",
  "removaljobs_sendquote",
  "salesorder_sendquote_paymentcard",
  "installationjobs_sendquote_paymentcard",
  "removaljobs_sendquote_paymentcard",
  // "New_project_create",
  "Design_approved_quote_reminder",
  "Signage_Design_approved_quote_reminder",
  "endusers_conversion",
  "Invoice_for_order",
  "Invoice_for_Batch_Payment",
]);

function emailCtaLoginUrl(): string {
  return `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/login`;
}

/**
 * CSS class to inline style mapping for email compatibility
 * Email clients often strip <style> tags, so we convert classes to inline styles
 */
const classToInlineStyles: Record<string, string> = {
  "email-heading-h2": "margin: 0 0 16px 0; color: #111827; font-size: 24px; font-weight: 500; font-family: 'Roboto', Arial, sans-serif; word-break: break-word; overflow-wrap: anywhere; max-width: 100%;",
  "email-heading-h3": "margin: 0 0 16px 0; color: #111827; font-size: 20px; font-weight: 500; font-family: 'Roboto', Arial, sans-serif;",
  "email-paragraph": "margin: 0 0 32px 0; color: #000000; font-size: 16px; line-height: 24px; font-family: 'Roboto', Arial, sans-serif; word-break: break-word; overflow-wrap: anywhere; max-width: 100%; font-weight: 400;",
  "email-paragraph-small": "margin: 0 0 24px 0; color: #000000; font-size: 16px; line-height: 24px; font-family: 'Roboto', Arial, sans-serif; word-break: break-word; overflow-wrap: anywhere; max-width: 100%; font-weight: 400;",
  "email-order-details-card": "background-color: #f9fafb; border-radius: 8px; padding: 25px; margin: 0 0 32px 0; border: 1px solid #e5e7eb; overflow: hidden; box-sizing: border-box; max-width: 100%;",
  "email-order-details-table": "width: 100%; border-collapse: collapse; table-layout: fixed; max-width: 100%; box-sizing: border-box;",
  "email-order-details-field": "padding: 8px 0; color: #6b7280; font-size: 14px; vertical-align: top; width: 35%; word-wrap: break-word; border-top: 1px solid #e5e7eb;",
  "email-order-details-value": "padding: 8px 0; color: black; font-size: 14px; font-weight: 500; vertical-align: top; border-top: 1px solid #e5e7eb; word-break: break-word; overflow-wrap: break-word; max-width: 0;",
  "tracking":"color: #155dfc; font-weight: 500;",
  "email-order-details-value-first": "padding: 8px 0; color: #111827; font-size: 13px; font-weight: 600; vertical-align: top; word-break: break-word; overflow-wrap: break-word; max-width: 0;",
  "email-order-details-value-bold": "padding: 8px 0; color: #111827; font-size: 13px; font-weight: 600; vertical-align: top; word-break: break-word; overflow-wrap: break-word; max-width: 0; border-top: 1px solid #e5e7eb;",
  "email-order-details-value-bold-first": "padding: 8px 0; color: #111827; font-size: 13px; font-weight: 600; vertical-align: top; word-break: break-word; overflow-wrap: break-word; max-width: 0;",
  "email-order-details-value-link": "padding: 8px 0; color: #2563EB; font-size: 13px; font-weight: 600; vertical-align: top; border-top: 1px solid #e5e7eb; word-break: break-all; overflow-wrap: anywhere; max-width: 0;",
  "email-button": "display:block; width:fit-content; margin:24px auto 0; padding:12px 32px; background-color:#2563EB; color:#ffffff !important; font-size:16px; font-weight:500; text-decoration:none; border-radius:8px; font-family:'Roboto',Arial,sans-serif; text-align:center;",
  "email-order-details-field-first": "padding: 8px 0; color: #6b7280; font-size: 13px; vertical-align: top; width: 35%; word-wrap: break-word;",
  "email-footer": "background-color: #f3f4f6; padding: 28px 24px 24px; border-top: 1px solid #e5e7eb; text-align: center; width: 100%; box-sizing: border-box;",
  "email-footer-link": "color: #64748b; font-size: 13px; text-decoration: none; padding: 0 6px;",
  "email-footer-separator": "color: #cbd5e1; font-size: 13px;",
  "email-footer-text": "margin: 0; color: #000000; font-size: 12px; line-height: 18px; font-weight: 400; font-family: 'Roboto', Arial, Helvetica, sans-serif; text-align: center;",
  "email-footer-social-icon": "display: inline-block; text-decoration: none; line-height: 0; padding: 4px; vertical-align: middle;",
  "email-footer-social-img": "display: block; width: 32px; height: 32px; max-width: 32px; max-height: 32px; border: 0; outline: none;",
  "email-footer-powered": "margin: 0; padding: 0; font-size: 11px; line-height: 1.5; color: #94a3b8; font-family: 'Roboto', Arial, Helvetica, sans-serif; text-align: center;",
  "email-footer-powered-lead": "color: #000000; font-weight: 400;",
  "email-footer-powered-brand": "color: #2563eb; text-decoration: none;",
};

/**
 * Convert CSS classes to inline styles for email compatibility
 * Email clients strip <style> tags, so we need inline styles
 */
function convertClassesToInlineStyles(html: string): string {
  let converted = html;
  
  // Process each opening tag that has a class attribute
  converted = converted.replace(/<([a-zA-Z][a-zA-Z0-9]*)\s*([^>]*)>/gi, (match, tagName, allAttrs) => {
    // Check if this tag has a class attribute
    const classMatch = allAttrs.match(/class=["']([^"']+)["']/i);
    if (!classMatch) {
      // No class attribute, return as-is
      return match;
    }
    
    const classList = classMatch[1];
    const classes = classList.split(/\s+/).filter((c: string) => c.trim());
    let inlineStyles: string[] = [];
    
    // Collect inline styles for all matching classes
    for (const className of classes) {
      if (classToInlineStyles[className]) {
        inlineStyles.push(classToInlineStyles[className]);
      }
    }
    
    if (inlineStyles.length === 0) {
      // No matching classes, return as-is
      return match;
    }
    
    // Remove class attribute
    let attrsWithoutClass = allAttrs.replace(/class=["'][^"']*["']/i, '').trim();
    
    // Check if there's already a style attribute
    const existingStyleMatch = allAttrs.match(/style=["']([^"']*)["']/i);
    if (existingStyleMatch) {
      // Merge with existing styles
      const existingStyle = existingStyleMatch[1];
      const allStyles = [existingStyle, ...inlineStyles].join('; ');
      
      // Remove old style attribute and add new combined one
      attrsWithoutClass = attrsWithoutClass.replace(/style=["'][^"']*["']/i, '').trim();
      // Keep class so <style> media queries can fix layout on mobile (inline still helps clients that strip <style>)
      return `<${tagName} class="${classList}" style="${allStyles}"${attrsWithoutClass ? ' ' + attrsWithoutClass : ''}>`;
    } else {
      // Add style attribute
      const allStyles = inlineStyles.join('; ');
      return `<${tagName} class="${classList}" style="${allStyles}"${attrsWithoutClass ? ' ' + attrsWithoutClass : ''}>`;
    }
  });
  
  return converted;
}

/** Social URLs from marketplace profile `social_links` (email footer: Facebook, Instagram, LinkedIn only) */
export type VendorEmailSocialLinks = {
  facebook?: string;
  instagram?: string;
  linkedin?: string;
};

/** Keys merged into email footers (order matches footer icon row). */
const VENDOR_EMAIL_SOCIAL_KEYS = ["facebook", "instagram", "linkedin"] as const;
type VendorEmailSocialKey = (typeof VENDOR_EMAIL_SOCIAL_KEYS)[number];

export type EmailBrandingOptions = {
  /** Absolute https URL for img src (e.g. presigned work-drive URL) */
  headerLogoUrl?: string;
  headerLogoAlt?: string;
  socialLinks?: VendorEmailSocialLinks | null;
  /** Display name when present (e.g. portal header). */
  vendorName?: string;
  /**
   * Shown after "Thanks," in the email signature — from template context or `userName` cookie.
   */
  signatureDisplayName?: string;
  /** From email template `customer_message` — optional line above footer social icons. */
  footerCustomerMessage?: string;
};

function escapeHtmlAttr(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Escape text placed inside HTML body (e.g. signature name). */
function escapeHtmlText(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Ensure social / website hrefs are usable in email clients */
function normalizeHref(href: string): string {
  const t = href.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  if (/^mailto:/i.test(t) || /^tel:/i.test(t)) return t;
  return `https://${t.replace(/^\/+/, "")}`;
}

/**
 * Email clients need an absolute http(s) image URL. Webpack `/static/media/...` and bare paths break in Gmail/mobile.
 */
function resolveEmailAbsoluteAssetUrl(pathOrUrl: string): string {
  const s = pathOrUrl.trim();
  if (!s) return s;
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) {
    return s;
  }
  const pathOnly = s.startsWith("/") ? s : `/${s}`;

  const bases: string[] = [];
  const winOrigin =
    typeof window !== "undefined" && window.location?.origin
      ? String(window.location.origin).replace(/\/+$/, "")
      : "";
  const publicUrl = (process.env.PUBLIC_URL || "").trim().replace(/\/+$/, "");
  if (publicUrl.startsWith("http://") || publicUrl.startsWith("https://")) {
    bases.push(publicUrl);
  } else if (winOrigin) {
    if (publicUrl && publicUrl !== "/" && publicUrl !== ".") {
      const p = publicUrl.startsWith("/") ? publicUrl : `/${publicUrl}`;
      bases.push(`${winOrigin}${p}`.replace(/\/+$/, ""));
    } else {
      bases.push(winOrigin);
    }
  }

  for (const b of [
    process.env.REACT_APP_EMAIL_ASSET_BASE_URL,
    process.env.REACT_APP_BASE_URL,
    process.env.REACT_APP_PUBLIC_WEB_URL,
  ]) {
    if (b && String(b).trim()) bases.push(String(b).replace(/\/+$/, ""));
  }

  const seen = new Set<string>();
  const uniq = bases.filter((b) => {
    if (!b || seen.has(b)) return false;
    seen.add(b);
    return true;
  });

  if (uniq.length > 0) {
    return `${uniq[0]}${pathOnly}`;
  }
  const isPublicSocialIcon =
    pathOnly.startsWith("/social_icons/") || pathOnly.startsWith("/assets/email/");
  try {
    const api = (process.env.REACT_APP_API_URL || "").trim();
    if (!isPublicSocialIcon && api.startsWith("http")) {
      return `${new URL(api).origin}${pathOnly}`;
    }
  } catch {
    /* ignore */
  }
  console.warn(
    "[emailTemplate] Asset URL is relative and no origin could be resolved — set REACT_APP_BASE_URL / REACT_APP_EMAIL_ASSET_BASE_URL, REACT_APP_EMAIL_SOCIAL_ICONS_BASE_URL, or send from the deployed app. Path:",
    pathOnly
  );
  return pathOnly;
}

function getBundledDefaultLogoUrl(): string {
  let logoPath: string;
  if (typeof logo === "string") {
    logoPath = logo;
  } else if (logo && typeof logo === "object") {
    const logoAny = logo as any;
    logoPath = logoAny?.default ? String(logoAny.default) : "/assets/images/Black_Version.png";
  } else {
    logoPath = "/assets/images/Black_Version.png";
  }
  if (logoPath.startsWith("http://") || logoPath.startsWith("https://")) {
    return logoPath;
  }
  if (logoPath.startsWith("data:") || logoPath.startsWith("blob:")) {
    return logoPath;
  }
  return resolveEmailAbsoluteAssetUrl(logoPath);
}

/**
 * Footer social icons: same paths as `public/assets/email/*.png` and `PublicEmailAsset` routes.
 * PNGs are 64×64 (@2x); HTML displays at 32×32 so forwarded mail still looks correct if clients strip styles.
 *
 * Prefer `REACT_APP_EMAIL_FOOTER_ICON_BASE_URL` (e.g. `https://app.dev.realesy.com`) so emails use a
 * **live HTTPS** origin for `<img src>` — not `http://app.local.realesy.com:3000` (blocked in Gmail).
 * Falls back to `resolveEmailAbsoluteAssetUrl` when unset.
 */
function getEmailFooterPublicAssetIconUrl(key: VendorEmailSocialKey): string {
  const path = `/assets/email/${key}.png`;
  const fixed = (process.env.REACT_APP_EMAIL_FOOTER_ICON_BASE_URL || "").trim().replace(/\/+$/, "");
  if (fixed.startsWith("http://") || fixed.startsWith("https://")) {
    return `${fixed}${path}`;
  }
  return resolveEmailAbsoluteAssetUrl(path);
}

/**
 * Absolute `src` URLs for standalone HTML templates (e.g. `password.html`) that use
 * `{{linkedinIconUrl}}`, `{{facebookIconUrl}}`, `{{instagramIconUrl}}` on footer `<img>` tags.
 * Merge into template params before sending email so icons load in Gmail and in in-app preview.
 */
export function getStandaloneEmailFooterIconUrlParams(): {
  linkedinIconUrl: string;
  facebookIconUrl: string;
  instagramIconUrl: string;
} {
  return {
    linkedinIconUrl: getEmailFooterPublicAssetIconUrl("linkedin"),
    facebookIconUrl: getEmailFooterPublicAssetIconUrl("facebook"),
    instagramIconUrl: getEmailFooterPublicAssetIconUrl("instagram"),
  };
}

/**
 * Content-ID names for inline MIME images. HTML uses `src="cid:NAME"`; the mailer attaches the file with the same `cid`.
 * Use {@link EmailInlineAttachmentHint} from {@link generateEmailHTML} so the backend can fetch bytes at send time
 * (avoids broken header images when S3 presigned URLs expire in the HTML body).
 */
export const EMAIL_CID_HEADER_LOGO = "realesy_email_header_logo";

export const EMAIL_CID_FOOTER_SOCIAL: Record<VendorEmailSocialKey, string> = {
  facebook: "realesy_email_footer_facebook",
  instagram: "realesy_email_footer_instagram",
  linkedin: "realesy_email_footer_linkedin",
};

export type EmailInlineAttachmentHint = {
  /** Same value as Nodemailer `attachments[].cid` (no `cid:` prefix). */
  cid: string;
  /** Download bytes from this URL when sending (HTTPS presigned S3, CDN, or absolute app asset URL). */
  sourceUrl: string;
  /** Suggested filename for the MIME part. */
  filename?: string;
};

/** Header logo only. Footer social icons use public `https://…/assets/email/*.png` in HTML (no CID). */
function buildEmailInlineAttachmentHints(headerLogoSourceUrl: string): EmailInlineAttachmentHint[] {
  return [
    {
      cid: EMAIL_CID_HEADER_LOGO,
      sourceUrl: headerLogoSourceUrl,
      filename: "email-header-logo",
    },
  ];
}

/** Display size in HTML; source PNGs are 64×64 (@2x) under public/assets/email/. */
const EMAIL_FOOTER_SOCIAL_ICON_PX = 32;
const EMAIL_FOOTER_SOCIAL_ICON_STYLE =
  "display:block;width:32px;height:32px;max-width:32px;max-height:32px;border:0;outline:none;text-decoration:none;";

/** Footer always shows these three networks (PNG from /assets/email/). */
const EMAIL_FOOTER_SOCIAL_ORDER: ReadonlyArray<{ key: VendorEmailSocialKey; title: string }> = [
  { key: "facebook", title: "Facebook" },
  { key: "instagram", title: "Instagram" },
  { key: "linkedin", title: "LinkedIn" },
];

/**
 * Resolve work-drive / storage file payload to a usable image URL.
 * Pass session `token` when `logo` is a 24-char file id so the header image works in emails.
 */
function cloudFileRecordToUrl(cf: { url?: string; raw?: unknown }): string | undefined {
  if (cf.url && String(cf.url).trim()) return String(cf.url).trim();
  const r = cf.raw as any;
  return (
    r?.url ||
    r?.signedUrl ||
    r?.presignedUrl ||
    r?.presigned_url ||
    r?.data?.url ||
    r?.data?.signedUrl ||
    r?.data?.presignedUrl ||
    undefined
  );
}

/** Optional JSON in trigger params: {"facebook":"https://...","linkedin":"..."} */
export function parseVendorSocialLinksFromParams(
  params: Record<string, string>
): VendorEmailSocialLinks | undefined {
  const raw = params.email_social_links_json?.trim();
  if (!raw) return undefined;
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    if (!o || typeof o !== "object") return undefined;
    const out: VendorEmailSocialLinks = {};
    VENDOR_EMAIL_SOCIAL_KEYS.forEach((k) => {
      const v = o[k];
      if (typeof v === "string" && v.trim()) out[k] = v.trim();
    });
    return Object.keys(out).length ? out : undefined;
  } catch {
    console.warn("[emailTemplate] email_social_links_json is not valid JSON; ignoring.");
    return undefined;
  }
}

/**
 * Load email template branding (public) for `GET .../public/email-templates/organizations/:organizationId`.
 * `createdByUserId` when set is used as organization id; otherwise {@link resolveEmailTemplateBrandingUserId}.
 * Signature name prefers template `company_name` / `vendor_name`, then the signed-in person name.
 * `token` is used if the API returns a logo file id without `company_logo_presigned_url`.
 */
export async function fetchVendorEmailBrandingByCreatorUserId(
  createdByUserId: string,
  token: string | null
): Promise<EmailBrandingOptions> {
  const api = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const userKey = resolveEmailTemplateBrandingUserId(createdByUserId);
  if (!api || !userKey) {
    console.log("[emailTemplate] fetchEmailTemplateBranding: skipped (no API URL or empty user id).");
    return {};
  }

  const personName = String(getAuthCookie("userName") || "").trim();
  let companyName = "";

  let headerLogoUrl: string | undefined;
  let socialLinks: VendorEmailSocialLinks | undefined;
  let footerCustomerMessage: string | undefined;

  try {
    console.log("[emailTemplate] fetchPublicEmailTemplateByOrganizationId", userKey);
    const profile = await fetchPublicEmailTemplateByOrganizationId(userKey);
    if (profile && typeof profile === "object") {
      const sl = (profile as any).social_links;
      const links: VendorEmailSocialLinks = {};
      if (sl && typeof sl === "object") {
        VENDOR_EMAIL_SOCIAL_KEYS.forEach((k) => {
          const v = typeof sl[k] === "string" ? sl[k].trim() : "";
          if (v) links[k] = v;
        });
      }
      if (Object.keys(links).length) socialLinks = links;

      const presigned = typeof (profile as any).company_logo_presigned_url === "string"
        ? String((profile as any).company_logo_presigned_url).trim()
        : "";
      if (presigned && /^https?:\/\//i.test(presigned)) {
        headerLogoUrl = presigned;
      } else {
        const rawLogo = (profile as any).company_logo;
        if (typeof rawLogo === "string") {
          const L = rawLogo.trim();
          if (/^https?:\/\//i.test(L)) {
            headerLogoUrl = L;
          } else if (/^[0-9a-f]{24}$/i.test(L) && token) {
            try {
              const cf = await getCloudFileById(L, token);
              headerLogoUrl = cloudFileRecordToUrl(cf);
              if (!headerLogoUrl) {
                console.warn("[emailTemplate] Work-drive file resolved but no image URL in response for id", L);
              }
            } catch (e) {
              console.warn("[emailTemplate] getCloudFileById failed for logo id", L, e);
              headerLogoUrl = undefined;
            }
          } else if (L.startsWith("/")) {
            headerLogoUrl = resolveEmailAbsoluteAssetUrl(L);
          }
        }
      }

      if (!companyName) {
        const profileCompany =
          typeof (profile as any).company_name === "string"
            ? String((profile as any).company_name).trim()
            : typeof (profile as any).vendor_name === "string"
              ? String((profile as any).vendor_name).trim()
              : "";
        if (profileCompany) companyName = profileCompany;
      }

      const customerRaw =
        typeof (profile as any).customer_message === "string"
          ? String((profile as any).customer_message).trim()
          : "";
      if (customerRaw) footerCustomerMessage = customerRaw;
    } else {
      console.warn("[emailTemplate] fetchEmailTemplateBranding: empty or invalid template payload.");
    }
  } catch (e) {
    console.warn("[emailTemplate] fetchEmailTemplateBranding error:", e);
  }

  // Thanks signature: template company name, else person name
  const signatureDisplayName = companyName || personName || "";
  const displayForAlt = signatureDisplayName || "Vendor";

  const out: EmailBrandingOptions = {
    headerLogoUrl,
    headerLogoAlt: displayForAlt,
    vendorName: companyName || personName || undefined,
    signatureDisplayName: signatureDisplayName || undefined,
    socialLinks,
    footerCustomerMessage,
  };
  console.log("[emailTemplate] fetchEmailTemplateBranding result:", {
    hasLogoUrl: !!out.headerLogoUrl,
    socialKeys: out.socialLinks ? Object.keys(out.socialLinks) : [],
    companyName: companyName || null,
    signatureDisplayName: out.signatureDisplayName || null,
    hasFooterMessage: !!out.footerCustomerMessage,
  });
  return out;
}

/**
 * Header logo always uses CID in the final email HTML.
 * The backend mail API downloads the `sourceUrl` at send time and embeds it as an inline MIME part,
 * which avoids later breakage from expiring Work Drive presigned URLs.
 */
function generateEmailHeader(altText = "RealEsy", imgSrcEscaped: string): string {
  const alt = escapeHtmlAttr(altText || "RealEsy");
  return `
            <!-- Header logo always uses cid realesy_email_header_logo + inline attachment -->
            <tr>
              <td align="center" style="padding:32px 20px;border-bottom:1px solid #e5e7eb;">
                <img src="${imgSrcEscaped}" alt="${alt}" style="height:auto;width:140px;max-width:140px;display:block;margin:0 auto;object-fit:contain;border:0;outline:none;text-decoration:none;" />
              </td>
            </tr>`;
}

/** Match `Order_summary` powered-by defaults for email footers */
const EMAIL_POWERED_BY_TEXT = "Powered by RealEsy";
const EMAIL_POWERED_BY_PREFIX = "Powered by ";
const EMAIL_POWERED_BY_BRAND = "RealEsy";
const EMAIL_POWERED_BY_URL = "https://realesy.com/";

function buildFooterSocialCells(socialLinks?: VendorEmailSocialLinks | null): string[] {
  return EMAIL_FOOTER_SOCIAL_ORDER.map(({ key, title }) => {
    const raw = socialLinks?.[key]?.trim();
    const href = raw ? normalizeHref(raw) : "#";
    const h = escapeHtmlAttr(href || "#");
    const t = escapeHtmlAttr(title);
    /** Public HTTPS asset URLs — Gmail loads these without relying on CID inline parts from the mail API. */
    const src = escapeHtmlAttr(getEmailFooterPublicAssetIconUrl(key));
    const px = EMAIL_FOOTER_SOCIAL_ICON_PX;
    return `<td class="email-footer-social-cell" align="center" width="52" style="width:52px;padding:0 10px;vertical-align:middle;text-align:center;">
                            <a href="${h}" class="email-footer-social-icon" title="${t}" aria-label="${t}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:block;line-height:0;width:${px}px;height:${px}px;margin:0 auto;">
                              <img src="${src}" alt="${t}" width="${px}" height="${px}" border="0" class="email-footer-social-img" style="${EMAIL_FOOTER_SOCIAL_ICON_STYLE}" />
                            </a>
                          </td>`;
  });
}

function buildEmailFooterPoweredByHtml(): string {
  const url = EMAIL_POWERED_BY_URL.trim();
  const brand = escapeHtmlAttr(EMAIL_POWERED_BY_BRAND);
  const prefix = escapeHtmlAttr(EMAIL_POWERED_BY_PREFIX);
  const full = escapeHtmlAttr(EMAIL_POWERED_BY_TEXT);
  if (url) {
    const href = escapeHtmlAttr(url);
    return `<p class="email-footer-powered"><span class="email-footer-powered-lead">${prefix}</span><a href="${href}" class="email-footer-powered-brand" target="_blank" rel="noopener noreferrer">${brand}</a></p>`;
  }
  return `<p class="email-footer-powered">${full}</p>`;
}

/**
 * Generate email footer — social icons use public HTTPS asset URLs; link URLs when provided else "#".
 */
function generateEmailFooter(
  footerText: string,
  socialLinks?: VendorEmailSocialLinks | null,
  customerMessage?: string | null
): string {
  const cells = buildFooterSocialCells(socialLinks);

  const customerRow =
    customerMessage && String(customerMessage).trim()
      ? `<tr>
                    <td align="center" width="100%" style="padding:16px 16px 8px;text-align:center;width:100%;">
                      <p class="email-footer-customer" style="margin:0;color:#374151;font-size:14px;line-height:22px;font-family:'Roboto',Arial,Helvetica,sans-serif;text-align:center;max-width:520px;margin-left:auto;margin-right:auto;">
                        ${escapeHtmlText(String(customerMessage).trim())}
                      </p>
                    </td>
                  </tr>`
      : "";

  const socialRow = `<tr>
                    <td class="email-footer-social-outer" align="center" width="100%" style="padding:18px 16px 20px;text-align:center;width:100%;">
                      <table class="email-footer-social-table" align="center" cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin:0 auto;border-collapse:collapse;text-align:center;">
                        <tr>
                          ${cells.join("")}
                        </tr>
                      </table>
                    </td>
                  </tr>`;

  const poweredRow = `<tr>
                    <td align="center" class="email-footer-powered-wrap" width="100%" style="padding-top:12px;padding-bottom:4px;text-align:center;width:100%;">
                      ${buildEmailFooterPoweredByHtml()}
                    </td>
                  </tr>`;

  return `
            <!-- Footer -->
            <tr>
              <td class="email-footer">
                <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;width:100%;text-align:center;">
                  ${customerRow}
                  ${socialRow}
                  <tr>
                    <td align="center" class="email-footer-text-wrap" width="100%" style="padding:8px 16px 4px;text-align:center;width:100%;">
                      <p class="email-footer-text">
                        ${footerText}
                      </p>
                    </td>
                  </tr>
                  ${poweredRow}
                </table>
              </td>
            </tr>`;
}


/**
 * Builds { subject, html, inlineAttachments } with table-based email layout.
 * - Header: logo via `cid:` only; source bytes described in `inlineAttachments` (vendor URL or default asset)
 * - Footer: social icons via absolute `https://…/assets/email/*.png` (no CID; reliable in Gmail)
 * - Footer: optional vendor social link URLs from `branding.socialLinks`
 * - Dynamic year in footer
 * - Body content based on trigger and license type
 *
 * `sendTriggerEmails` loads branding from `GET .../public/email-templates/organizations/:id`
 * (optional params override: `email_marketplace_created_by`; id otherwise from license type + cookies).
 *
 * **Inline images:** When the header uses `cid:` (no absolute http(s) URL), `inlineAttachments` lists the logo
 * `cid` + `sourceUrl` for the mail API to attach. If the header uses a direct `https?://` URL, `inlineAttachments`
 * may be empty. Footer icons do not use CID.
 */
export function generateEmailHTML(
  triggerName: string,
  licenseType: string,
  params: Record<string,string>,
  branding?: EmailBrandingOptions
): { subject: string; html: string; inlineAttachments: EmailInlineAttachmentHint[] } {
  const cfg: TriggerConfig | undefined = triggerConfig[triggerName];
  console.log("generateEmailHTML - triggerName:", triggerName, "licenseType:", licenseType, "cfg:", cfg);
  if (!cfg) throw new Error(`Unknown trigger "${triggerName}"`);

  // 1) Build body HTML based on trigger-specific content structure
  let bodyHtml: string;
  
  // Check if bodyHtml is provided (takes priority)
  if (cfg.bodyHtml) {
    // Handle bodyHtml as string or Record<string,string>
    if (typeof cfg.bodyHtml === "string") {
      bodyHtml = interpolate(cfg.bodyHtml, params);
    } else {
      // It's a Record, get the one for this licenseType
      const htmlForType = cfg.bodyHtml[licenseType];
      if (htmlForType) {
        bodyHtml = interpolate(htmlForType, params);
      } else {
        throw new Error(`No bodyHtml for "${licenseType}" under "${triggerName}"`);
      }
    }
    // Convert CSS classes to inline styles for email compatibility
    bodyHtml = convertClassesToInlineStyles(bodyHtml);
  } else if (cfg.bodyLines) {
    // Fallback to bodyLines if bodyHtml is not provided
    let lines: string[];
    if (Array.isArray(cfg.bodyLines)) {
      lines = cfg.bodyLines;
    } else {
      lines = cfg.bodyLines[licenseType];
      if (!lines) {
        throw new Error(`No bodyLines for "${licenseType}" under "${triggerName}"`);
      }
    }
    // Interpolate and wrap each line in a paragraph
    const interpolatedLines = lines.map(line => interpolate(line, params));
    bodyHtml = interpolatedLines.map(line => `<p style="margin:0 0 1em;color:#4b5563;font-size:16px;line-height:24px;font-family:'Roboto',Arial,sans-serif;">${line}</p>`).join("");
  } else {
    throw new Error(`No body content defined for trigger "${triggerName}"`);
  }

  // 2) optional CTA button - interpolate URL if it contains placeholders
  // Handle CTA as object or Record for license-type-specific CTAs
  let ctaHtml = "";
  if (cfg.cta) {
    let cta: { text: string; url: string };
    if (typeof cfg.cta === "object" && !("text" in cfg.cta)) {
      // It's a Record, get the one for this licenseType
      const ctaForType = cfg.cta[licenseType];
      if (ctaForType) {
        cta = ctaForType;
      } else {
        // Fallback to first available CTA
        const firstCta = Object.values(cfg.cta)[0];
        cta = firstCta || { text: "", url: "" };
      }
    } else {
      // It's a single CTA object
      cta = cfg.cta as { text: string; url: string };
    }
    
    if (cta && cta.text && cta.url) {
      const interpolatedConfiguredUrl = interpolate(cta.url, params);
      const hasResolvedDynamicUrl =
        /\S/.test(String(interpolatedConfiguredUrl || "")) &&
        !String(interpolatedConfiguredUrl).includes("{{");
      const ctaUrlSource =
        TRIGGER_CTA_KEEP_CONFIGURED_URL.has(triggerName) || hasResolvedDynamicUrl
          ? cta.url
          : emailCtaLoginUrl();
      const ctaUrl = interpolate(ctaUrlSource, params);
      ctaHtml = `<div style="margin:32px 0;text-align:center;">
         <a href="${ctaUrl}"
            style="
              display:inline-block;
              padding:12px 32px;
              background-color:#2563EB;
              color:#ffffff;
              font-size:16px;
              font-weight:500;
              text-decoration:none;
              border-radius:8px;
              font-family:'Roboto',Arial,sans-serif;
            ">
           ${cta.text}
         </a>
       </div>`;
    }
  }

  // 3) Handle subject - can be string or Record<string,string> for license-type-specific subjects
  // Must be resolved BEFORE using in HTML template
  let subject: string;
  if (typeof cfg.subject === "string") {
    subject = interpolate(cfg.subject, params);
  } else {
    // It's a Record, get the one for this licenseType
    const subjectForType = cfg.subject[licenseType];
    if (subjectForType) {
      subject = interpolate(subjectForType, params);
    } else {
      // Fallback to first available subject or default
      const firstSubject = Object.values(cfg.subject)[0];
      subject = firstSubject ? interpolate(firstSubject, params) : cfg.title;
    }
  }

  // 4) Dynamic year and footer text
  const year = new Date().getFullYear();
  const footerText = `© ${year} ${cfg.footer}`;

  // 5) Generate header and footer (optional vendor logo + social from marketplace / params)
  const explicitLogo = params.email_header_logo_url?.trim();
  let vendorLogoUrl = explicitLogo || branding?.headerLogoUrl?.trim();
  if (vendorLogoUrl && !/^https?:\/\//i.test(vendorLogoUrl) && !vendorLogoUrl.startsWith("data:")) {
    vendorLogoUrl = resolveEmailAbsoluteAssetUrl(vendorLogoUrl);
  }
  const useVendorLogo = !!vendorLogoUrl;
  const defaultLogoResolved = getBundledDefaultLogoUrl();
  const headerLogoSourceUrl = useVendorLogo && vendorLogoUrl ? vendorLogoUrl : defaultLogoResolved;
  const headerImgEscaped = escapeHtmlAttr(`cid:${EMAIL_CID_HEADER_LOGO}`);
  const emailHeader = generateEmailHeader(
    useVendorLogo ? (branding?.headerLogoAlt || "Vendor") : "RealEsy",
    headerImgEscaped
  );
  /** When the header uses a direct `https?://` src, skip CID attachments (mail API often fails to map cid → MIME for Gmail). */
  const inlineAttachments = buildEmailInlineAttachmentHints(headerLogoSourceUrl);
  const headerUseDirectUrl = false;

  const socialFromParams = parseVendorSocialLinksFromParams(params);
  const mergedSocialInput: VendorEmailSocialLinks = {
    ...(branding?.socialLinks || {}),
    ...(socialFromParams || {}),
  };
  const mergedSocial: VendorEmailSocialLinks | undefined = (() => {
    const o: VendorEmailSocialLinks = {};
    VENDOR_EMAIL_SOCIAL_KEYS.forEach((k) => {
      const v = mergedSocialInput[k]?.trim();
      if (v) o[k] = v;
    });
    return Object.keys(o).length ? o : undefined;
  })();

  console.log("[emailTemplate] bodyHtml length:", bodyHtml.length, "snippet:", bodyHtml.slice(0, 160).replace(/\s+/g, " "));
  console.log(
    "[emailTemplate] header image:",
    useVendorLogo ? `vendor (${vendorLogoUrl?.slice(0, 80)}…)` : `default (${defaultLogoResolved.slice(0, 80)}…)`,
    headerUseDirectUrl ? "→ direct https src (no CID attachment)" : `→ cid ${EMAIL_CID_HEADER_LOGO} + inline attachment`
  );
  console.log(
    "[emailTemplate] footer social:",
    mergedSocial ? `dynamic URLs for: ${Object.keys(mergedSocial).join(", ")}` : "default # hrefs for Facebook, Instagram, LinkedIn icons"
  );

  let emailFooter = generateEmailFooter(
    footerText,
    mergedSocial,
    branding?.footerCustomerMessage?.trim() || undefined
  );
  // Convert footer CSS classes to inline styles
  emailFooter = convertClassesToInlineStyles(emailFooter);

  // Signature: firmDetails.companyName (branding), else param override, else "-"
  const signatureDisplayRaw =
    branding?.signatureDisplayName?.trim() ||
    params.email_signature_display_name?.trim() ||
    "-";
  const signatureDisplayEscaped = escapeHtmlText(signatureDisplayRaw);

  // 6) Build simple, responsive HTML using tables (best for email clients)
  // Header is ALWAYS included in the HTML template
  const html = `
<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8"/>
    <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
    <title>${subject}</title>
    <style>
      @media only screen and (max-width: 600px) {
        .email-wrapper { width: 100% !important; padding: 10px !important; }
        .email-card { width: 100% !important; max-width: 100% !important; }
        .email-content { padding: 24px 16px !important; }
        .email-footer {
          padding: 28px 20px 26px !important;
          text-align: center !important;
          width: 100% !important;
        }
        .email-footer > table { margin: 0 auto !important; width: 100% !important; }
        .email-footer-social-outer {
          padding: 22px 12px 22px !important;
          text-align: center !important;
          width: 100% !important;
        }
        .email-footer-social-table {
          width: auto !important;
          max-width: 100% !important;
          margin-left: auto !important;
          margin-right: auto !important;
          text-align: center !important;
        }
        .email-footer-social-cell { padding: 0 12px !important; text-align: center !important; }
        .email-footer-social-img {
          width: 32px !important;
          height: 32px !important;
          max-width: 32px !important;
          max-height: 32px !important;
        }
        .email-footer-text-wrap {
          padding: 12px 12px 8px !important;
          text-align: center !important;
          width: 100% !important;
        }
        .email-footer-text {
          line-height: 1.5 !important;
          padding: 0 8px !important;
          text-align: center !important;
        }
        .email-footer-powered-wrap {
          padding-top: 14px !important;
          padding-bottom: 8px !important;
          text-align: center !important;
          width: 100% !important;
        }
        .email-footer-powered { text-align: center !important; }
        .access-table { font-size: 12px !important; }
        .access-table td { padding: 6px 0 !important; }
        .email-heading-h2 { font-size: 22px !important; line-height: 1.3 !important; }
        .email-heading-h3 { font-size: 18px !important; line-height: 1.35 !important; display: block !important; }
        .email-paragraph, .email-paragraph-small {
          max-width: 100% !important;
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
        }
        .email-order-details-card {
          padding: 16px !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .email-order-details-card p {
          max-width: 100% !important;
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
        }
        .email-order-details-table { display: block !important; width: 100% !important; table-layout: auto !important; }
        .email-order-details-table tbody { display: block !important; width: 100% !important; }
        .email-order-details-table tr {
          display: block !important;
          width: 100% !important;
          margin-bottom: 12px !important;
          padding-bottom: 10px !important;
          border-bottom: 1px solid #e5e7eb !important;
          box-sizing: border-box !important;
        }
        .email-order-details-table tr:last-child { border-bottom: none !important; margin-bottom: 0 !important; padding-bottom: 0 !important; }
        .email-order-details-table td {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
          border-top: none !important;
          padding: 4px 0 !important;
        }
        .email-order-details-field,
        .email-order-details-field-first {
          width: 100% !important;
          max-width: 100% !important;
          color: #374151 !important;
          font-weight: 600 !important;
          font-size: 13px !important;
        }
        .email-order-details-value,
        .email-order-details-value-first,
        .email-order-details-value-bold,
        .email-order-details-value-bold-first,
        .email-order-details-value-link {
          width: 100% !important;
          max-width: 100% !important;
          font-size: 15px !important;
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
        }
      }
    .email-heading-h2 {
  margin: 0 0 16px 0;
  color: #111827;
  font-size: 24px;
  font-weight: 500;
  font-family: 'Roboto', Arial, sans-serif;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
}

.email-heading-h3 {
  margin: 0 0 16px 0;
  color: #111827;
  font-size: 20px;
  font-weight: 500;
  font-family: 'Roboto', Arial, sans-serif;
  display: block;
}

.email-paragraph {
  margin: 0 0 32px 0;
  // color: #4b5563;
  color: #000000;
  font-size: 16px;
  line-height: 24px;
  font-family: 'Roboto', Arial, sans-serif;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
  font-weight: 400;
}

.email-paragraph-small {
  margin: 0 0 24px 0;
  // color: #4b5563;
   color: #000000;
  font-size: 16px;
  line-height: 24px;
  font-family: 'Roboto', Arial, sans-serif;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
  font-weight: 400;
}

/* Order Details Card */
.email-order-details-card {
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 25px;
  margin: 0 0 32px 0;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-sizing: border-box;
  max-width: 100%;
}

.email-order-details-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  max-width: 100%;
  box-sizing: border-box;
}

.email-order-details-field {
  padding: 8px 0;
  color: #6b7280;
  font-size: 14px;
  vertical-align: top;
  width: 35%;
  word-wrap: break-word;
  border-top: 1px solid #e5e7eb;
}

.email-order-details-value {
  padding: 8px 0;
  color: black;
  font-size: 14px;
  font-weight: 500;
  vertical-align: top;
  border-top: 1px solid #e5e7eb !important;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 0;
}

.email-order-details-value-first {
  padding: 8px 0;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  vertical-align: top;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 0;
}

.email-order-details-value-bold {
  padding: 8px 0;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  vertical-align: top;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 0;
  border-top: 1px solid #e5e7eb !important;
}

.email-order-details-value-bold-first {
  padding: 8px 0;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  vertical-align: top;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 0;
}

.email-order-details-value-link {
  padding: 8px 0;
  color: #2563EB;
  font-size: 13px;
  font-weight: 600;
  vertical-align: top;
  border-top: 1px solid #e5e7eb !important;
  word-break: break-all;
  overflow-wrap: anywhere;
  max-width: 0;
}

.email-order-details-field-first {
  padding: 8px 0;
  color: #6b7280;
  font-size: 13px;
  vertical-align: top;
  width: 35%;
  word-wrap: break-word;
}

/* Line Items Card */
.email-line-items-heading {
  margin: 32px 0 16px 0;
  color: #111827;
  font-size: 20px;
  font-weight: 500;
  font-family: 'Roboto', Arial, sans-serif;
  display: flex;
  gap: 5px;
}

.email-line-items-container {
  background-color: #f9fafb !important;
  border-radius: 8px;
  padding: 16px;
  margin: 0 0 32px 0;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.email-line-item-row {
  padding: 12px 0;
  /* border-top: 1px solid #e5e7eb; */
  background-color: #f9fafb;
}

.email-line-item-row:first-child {
  padding: 0;
  border-top: none;
}

.email-line-item-table {
  width: 100%;
  border-collapse: collapse;
}

.email-line-item-product {
  padding: 0;
  vertical-align: top;
}

.email-line-item-product-name {
  color: black;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
}

.email-line-item-product-desc {
  color: #6b7280;
  font-size: 14px;
  line-height: 18px;
}

.email-line-item-qty {
  padding: 0 0 0 16px;
  vertical-align: top;
  text-align: right;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  color:black;
}

.email-line-item-qty-text {
  color: #111827;
  font-size: 13px;
  font-weight: 400;
}

/* Email Footer */
.email-footer {
  background-color: #f3f4f6;
  padding: 28px 24px 24px;
  border-top: 1px solid #e5e7eb;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
}

.email-footer-link {
  color: #64748b !important;
  font-size: 13px;
  text-decoration: none;
  padding: 0 6px;
}

.email-footer-separator {
  color: #cbd5e1;
  font-size: 13px;
}

.email-footer-text {
  margin: 0;
  color: #000000;
  font-size: 12px;
  line-height: 1.5;
  text-align: center;
}

.email-footer-social-outer {
  padding: 16px 16px 18px;
  text-align: center;
  width: 100%;
}

.email-footer-social-table {
  margin-left: auto;
  margin-right: auto;
  border-collapse: collapse;
  text-align: center;
}

.email-footer-social-cell {
  padding: 0 10px;
  vertical-align: middle;
  text-align: center;
}

.email-footer-social-icon {
  display: inline-block;
  text-decoration: none;
  line-height: 0;
  padding: 4px;
  vertical-align: middle;
}

.email-footer-social-img {
  display: block;
  width: 32px;
  height: 32px;
  max-width: 32px;
  max-height: 32px;
  border: 0;
  outline: none;
}

.email-footer-powered {
  margin: 0;
  padding: 0;
  font-size: 11px;
  line-height: 1.5;
  color: #94a3b8;
  font-family: 'Roboto', Arial, Helvetica, sans-serif;
  text-align: center;
}

.email-footer-powered-lead {
  color: #94a3b8;
}

.email-footer-powered-brand {
  color: #2563eb;
  font-weight: 600;
  text-decoration: none;
}
    </style>
  </head>
  <body style="margin:0;padding:0;background-color:#f4f4f4;font-family:Arial,sans-serif;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f4;">
      <tr>
        <td align="center" class="email-wrapper" style="padding:20px 10px;">
          <table width="600" cellpadding="0" cellspacing="0" class="email-card" style="max-width:100%;width:600px;background-color:#ffffff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.1);border: 1px solid #dedada;">
            ${emailHeader}

            <!-- Content -->
            <tr>
              <td class="email-content" style="padding:40px 20px;">
                ${bodyHtml}
                ${ctaHtml}
                
                <!-- Signature (vendor company_name || vendor_name from marketplace when branding loaded) -->
                <p style="margin:32px 0 0 0;color:#000000;font-size:16px;line-height:24px;font-family:'Roboto',Arial,sans-serif;">
                  Thanks,<br/>
                  <strong style="color:#111827;font-weight:500;">${signatureDisplayEscaped}</strong>
                </p>
              </td>
            </tr>

            ${emailFooter}
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;

  return { subject, html, inlineAttachments };
}






























// import { triggerConfig, TriggerConfig } from "./emailConfig";
// import logo from "../assets/images/Black_Version.png";
// import { getCloudFileById } from "components/Imageshelper";

// /** simple {{key}} replacement */
// function interpolate(str: string, params: Record<string,string>) {
//   return Object.entries(params)
//     .reduce((s, [k,v]) => s.replaceAll(`{{${k}}}`, v), str);
// }

// /**
//  * Triggers whose `triggerConfig` CTA `url` is kept as-is (e.g. {{viewPayUrl}}, {{reviewUrl}}).
//  * All other triggers use {@link emailCtaLoginUrl} for the primary CTA button only.
//  */
// const TRIGGER_CTA_KEEP_CONFIGURED_URL = new Set([
//   /** Order summary email: {{customerName}}, {{orderId}}, {{itemName}}, {{quantity}}, {{totalDue}}, {{alreadyPaid}}, {{remainingBalance}}, {{viewPayUrl}} */
//   "Your_order _is _ready",
//   "New_project_create",
//   "Design_approved_quote_reminder",
//   "Signage_Design_approved_quote_reminder",
//   "endusers_conversion",
// ]);

// function emailCtaLoginUrl(): string {
//   return `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/login`;
// }

// /**
//  * CSS class to inline style mapping for email compatibility
//  * Email clients often strip <style> tags, so we convert classes to inline styles
//  */
// const classToInlineStyles: Record<string, string> = {
//   "email-heading-h2": "margin: 0 0 16px 0; color: #111827; font-size: 24px; font-weight: 500; font-family: 'Roboto', Arial, sans-serif; word-break: break-word; overflow-wrap: anywhere; max-width: 100%;",
//   "email-heading-h3": "margin: 0 0 16px 0; color: #111827; font-size: 20px; font-weight: 500; font-family: 'Roboto', Arial, sans-serif;",
//   "email-paragraph": "margin: 0 0 32px 0; color: #000000; font-size: 16px; line-height: 24px; font-family: 'Roboto', Arial, sans-serif; word-break: break-word; overflow-wrap: anywhere; max-width: 100%; font-weight: 400;",
//   "email-paragraph-small": "margin: 0 0 24px 0; color: #000000; font-size: 16px; line-height: 24px; font-family: 'Roboto', Arial, sans-serif; word-break: break-word; overflow-wrap: anywhere; max-width: 100%; font-weight: 400;",
//   "email-order-details-card": "background-color: #f9fafb; border-radius: 8px; padding: 25px; margin: 0 0 32px 0; border: 1px solid #e5e7eb; overflow: hidden; box-sizing: border-box; max-width: 100%;",
//   "email-order-details-table": "width: 100%; border-collapse: collapse; table-layout: fixed; max-width: 100%; box-sizing: border-box;",
//   "email-order-details-field": "padding: 8px 0; color: #6b7280; font-size: 14px; vertical-align: top; width: 35%; word-wrap: break-word; border-top: 1px solid #e5e7eb;",
//   "email-order-details-value": "padding: 8px 0; color: black; font-size: 14px; font-weight: 500; vertical-align: top; border-top: 1px solid #e5e7eb; word-break: break-word; overflow-wrap: break-word; max-width: 0;",
//   "tracking":"color: #155dfc; font-weight: 500;",
//   "email-order-details-value-first": "padding: 8px 0; color: #111827; font-size: 13px; font-weight: 600; vertical-align: top; word-break: break-word; overflow-wrap: break-word; max-width: 0;",
//   "email-order-details-value-bold": "padding: 8px 0; color: #111827; font-size: 13px; font-weight: 600; vertical-align: top; word-break: break-word; overflow-wrap: break-word; max-width: 0; border-top: 1px solid #e5e7eb;",
//   "email-order-details-value-bold-first": "padding: 8px 0; color: #111827; font-size: 13px; font-weight: 600; vertical-align: top; word-break: break-word; overflow-wrap: break-word; max-width: 0;",
//   "email-order-details-value-link": "padding: 8px 0; color: #2563EB; font-size: 13px; font-weight: 600; vertical-align: top; border-top: 1px solid #e5e7eb; word-break: break-all; overflow-wrap: anywhere; max-width: 0;",
//   "email-order-details-field-first": "padding: 8px 0; color: #6b7280; font-size: 13px; vertical-align: top; width: 35%; word-wrap: break-word;",
//   "email-footer": "background-color: #f3f4f6; padding: 28px 24px 24px; border-top: 1px solid #e5e7eb; text-align: center; width: 100%; box-sizing: border-box;",
//   "email-footer-link": "color: #64748b; font-size: 13px; text-decoration: none; padding: 0 6px;",
//   "email-footer-separator": "color: #cbd5e1; font-size: 13px;",
//   "email-footer-text": "margin: 0; color: #000000; font-size: 12px; line-height: 18px; font-weight: 400; font-family: 'Roboto', Arial, Helvetica, sans-serif; text-align: center;",
//   "email-footer-social-icon": "display: inline-block; text-decoration: none; line-height: 0; padding: 4px; vertical-align: middle;",
//   "email-footer-social-img": "display: block; width: 32px; height: 32px; max-width: 32px; border: 0; outline: none;",
//   "email-footer-powered": "margin: 0; padding: 0; font-size: 11px; line-height: 1.5; color: #94a3b8; font-family: 'Roboto', Arial, Helvetica, sans-serif; text-align: center;",
//   "email-footer-powered-lead": "color: #000000; font-weight: 400;",
//   "email-footer-powered-brand": "color: #2563eb; text-decoration: none;",
// };

// /**
//  * Convert CSS classes to inline styles for email compatibility
//  * Email clients strip <style> tags, so we need inline styles
//  */
// function convertClassesToInlineStyles(html: string): string {
//   let converted = html;
  
//   // Process each opening tag that has a class attribute
//   converted = converted.replace(/<([a-zA-Z][a-zA-Z0-9]*)\s*([^>]*)>/gi, (match, tagName, allAttrs) => {
//     // Check if this tag has a class attribute
//     const classMatch = allAttrs.match(/class=["']([^"']+)["']/i);
//     if (!classMatch) {
//       // No class attribute, return as-is
//       return match;
//     }
    
//     const classList = classMatch[1];
//     const classes = classList.split(/\s+/).filter((c: string) => c.trim());
//     let inlineStyles: string[] = [];
    
//     // Collect inline styles for all matching classes
//     for (const className of classes) {
//       if (classToInlineStyles[className]) {
//         inlineStyles.push(classToInlineStyles[className]);
//       }
//     }
    
//     if (inlineStyles.length === 0) {
//       // No matching classes, return as-is
//       return match;
//     }
    
//     // Remove class attribute
//     let attrsWithoutClass = allAttrs.replace(/class=["'][^"']*["']/i, '').trim();
    
//     // Check if there's already a style attribute
//     const existingStyleMatch = allAttrs.match(/style=["']([^"']*)["']/i);
//     if (existingStyleMatch) {
//       // Merge with existing styles
//       const existingStyle = existingStyleMatch[1];
//       const allStyles = [existingStyle, ...inlineStyles].join('; ');
      
//       // Remove old style attribute and add new combined one
//       attrsWithoutClass = attrsWithoutClass.replace(/style=["'][^"']*["']/i, '').trim();
//       // Keep class so <style> media queries can fix layout on mobile (inline still helps clients that strip <style>)
//       return `<${tagName} class="${classList}" style="${allStyles}"${attrsWithoutClass ? ' ' + attrsWithoutClass : ''}>`;
//     } else {
//       // Add style attribute
//       const allStyles = inlineStyles.join('; ');
//       return `<${tagName} class="${classList}" style="${allStyles}"${attrsWithoutClass ? ' ' + attrsWithoutClass : ''}>`;
//     }
//   });
  
//   return converted;
// }

// /** Social URLs from marketplace profile `social_links` (email footer: Facebook, Instagram, LinkedIn only) */
// export type VendorEmailSocialLinks = {
//   facebook?: string;
//   instagram?: string;
//   linkedin?: string;
// };

// /** Keys merged into email footers (order matches footer icon row). */
// const VENDOR_EMAIL_SOCIAL_KEYS = ["facebook", "instagram", "linkedin"] as const;
// type VendorEmailSocialKey = (typeof VENDOR_EMAIL_SOCIAL_KEYS)[number];

// export type EmailBrandingOptions = {
//   /** Absolute https URL for img src (e.g. presigned work-drive URL) */
//   headerLogoUrl?: string;
//   headerLogoAlt?: string;
//   socialLinks?: VendorEmailSocialLinks | null;
//   /** Raw marketplace `vendor_name` when present (e.g. portal header). */
//   vendorName?: string;
//   /**
//    * Shown after "Thanks," in the email signature — from marketplace `company_name` || `vendor_name`.
//    */
//   signatureDisplayName?: string;
// };

// function escapeHtmlAttr(s: string): string {
//   return String(s)
//     .replace(/&/g, "&amp;")
//     .replace(/"/g, "&quot;")
//     .replace(/</g, "&lt;")
//     .replace(/>/g, "&gt;");
// }

// /** Escape text placed inside HTML body (e.g. signature name). */
// function escapeHtmlText(s: string): string {
//   return String(s)
//     .replace(/&/g, "&amp;")
//     .replace(/</g, "&lt;")
//     .replace(/>/g, "&gt;");
// }

// /** Ensure social / website hrefs are usable in email clients */
// function normalizeHref(href: string): string {
//   const t = href.trim();
//   if (!t) return "";
//   if (/^https?:\/\//i.test(t)) return t;
//   if (/^mailto:/i.test(t) || /^tel:/i.test(t)) return t;
//   return `https://${t.replace(/^\/+/, "")}`;
// }

// /**
//  * Email clients need an absolute http(s) image URL. Webpack `/static/media/...` and bare paths break in Gmail/mobile.
//  */
// function resolveEmailAbsoluteAssetUrl(pathOrUrl: string): string {
//   const s = pathOrUrl.trim();
//   if (!s) return s;
//   if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) {
//     return s;
//   }
//   const pathOnly = s.startsWith("/") ? s : `/${s}`;

//   const bases: string[] = [];
//   const winOrigin =
//     typeof window !== "undefined" && window.location?.origin
//       ? String(window.location.origin).replace(/\/+$/, "")
//       : "";
//   const publicUrl = (process.env.PUBLIC_URL || "").trim().replace(/\/+$/, "");
//   if (publicUrl.startsWith("http://") || publicUrl.startsWith("https://")) {
//     bases.push(publicUrl);
//   } else if (winOrigin) {
//     if (publicUrl && publicUrl !== "/" && publicUrl !== ".") {
//       const p = publicUrl.startsWith("/") ? publicUrl : `/${publicUrl}`;
//       bases.push(`${winOrigin}${p}`.replace(/\/+$/, ""));
//     } else {
//       bases.push(winOrigin);
//     }
//   }

//   for (const b of [
//     process.env.REACT_APP_EMAIL_ASSET_BASE_URL,
//     process.env.REACT_APP_BASE_URL,
//     process.env.REACT_APP_PUBLIC_WEB_URL,
//   ]) {
//     if (b && String(b).trim()) bases.push(String(b).replace(/\/+$/, ""));
//   }

//   const seen = new Set<string>();
//   const uniq = bases.filter((b) => {
//     if (!b || seen.has(b)) return false;
//     seen.add(b);
//     return true;
//   });

//   if (uniq.length > 0) {
//     return `${uniq[0]}${pathOnly}`;
//   }
//   const isPublicSocialIcon =
//     pathOnly.startsWith("/social_icons/") || pathOnly.startsWith("/assets/email/");
//   try {
//     const api = (process.env.REACT_APP_API_URL || "").trim();
//     if (!isPublicSocialIcon && api.startsWith("http")) {
//       return `${new URL(api).origin}${pathOnly}`;
//     }
//   } catch {
//     /* ignore */
//   }
//   console.warn(
//     "[emailTemplate] Asset URL is relative and no origin could be resolved — set REACT_APP_BASE_URL / REACT_APP_EMAIL_ASSET_BASE_URL, REACT_APP_EMAIL_SOCIAL_ICONS_BASE_URL, or send from the deployed app. Path:",
//     pathOnly
//   );
//   return pathOnly;
// }

// function getBundledDefaultLogoUrl(): string {
//   let logoPath: string;
//   if (typeof logo === "string") {
//     logoPath = logo;
//   } else if (logo && typeof logo === "object") {
//     const logoAny = logo as any;
//     logoPath = logoAny?.default ? String(logoAny.default) : "/assets/images/Black_Version.png";
//   } else {
//     logoPath = "/assets/images/Black_Version.png";
//   }
//   if (logoPath.startsWith("http://") || logoPath.startsWith("https://")) {
//     return logoPath;
//   }
//   if (logoPath.startsWith("data:") || logoPath.startsWith("blob:")) {
//     return logoPath;
//   }
//   return resolveEmailAbsoluteAssetUrl(logoPath);
// }

// /**
//  * Footer social icons: same paths as `public/assets/email/*.png` and `PublicEmailAsset` routes.
//  *
//  * Prefer `REACT_APP_EMAIL_FOOTER_ICON_BASE_URL` (e.g. `https://app.dev.realesy.com`) so emails use a
//  * **live HTTPS** origin for `<img src>` — not `http://app.local.realesy.com:3000` (blocked in Gmail).
//  * Falls back to `resolveEmailAbsoluteAssetUrl` when unset.
//  */
// function getEmailFooterPublicAssetIconUrl(key: VendorEmailSocialKey): string {
//   const path = `/assets/email/${key}.png`;
//   const fixed = (process.env.REACT_APP_EMAIL_FOOTER_ICON_BASE_URL || "").trim().replace(/\/+$/, "");
//   if (fixed.startsWith("http://") || fixed.startsWith("https://")) {
//     return `${fixed}${path}`;
//   }
//   return resolveEmailAbsoluteAssetUrl(path);
// }

// /**
//  * Absolute `src` URLs for standalone HTML templates (e.g. `password.html`) that use
//  * `{{linkedinIconUrl}}`, `{{facebookIconUrl}}`, `{{instagramIconUrl}}` on footer `<img>` tags.
//  * Merge into template params before sending email so icons load in Gmail and in in-app preview.
//  */
// export function getStandaloneEmailFooterIconUrlParams(): {
//   linkedinIconUrl: string;
//   facebookIconUrl: string;
//   instagramIconUrl: string;
// } {
//   return {
//     linkedinIconUrl: getEmailFooterPublicAssetIconUrl("linkedin"),
//     facebookIconUrl: getEmailFooterPublicAssetIconUrl("facebook"),
//     instagramIconUrl: getEmailFooterPublicAssetIconUrl("instagram"),
//   };
// }

// /**
//  * Content-ID names for inline MIME images. HTML uses `src="cid:NAME"`; the mailer attaches the file with the same `cid`.
//  * Use {@link EmailInlineAttachmentHint} from {@link generateEmailHTML} so the backend can fetch bytes at send time
//  * (avoids broken header images when S3 presigned URLs expire in the HTML body).
//  */
// export const EMAIL_CID_HEADER_LOGO = "realesy_email_header_logo";

// export const EMAIL_CID_FOOTER_SOCIAL: Record<VendorEmailSocialKey, string> = {
//   facebook: "realesy_email_footer_facebook",
//   instagram: "realesy_email_footer_instagram",
//   linkedin: "realesy_email_footer_linkedin",
// };

// export type EmailInlineAttachmentHint = {
//   /** Same value as Nodemailer `attachments[].cid` (no `cid:` prefix). */
//   cid: string;
//   /** Download bytes from this URL when sending (HTTPS presigned S3, CDN, or absolute app asset URL). */
//   sourceUrl: string;
//   /** Suggested filename for the MIME part. */
//   filename?: string;
// };

// /** Header logo only. Footer social icons use public `https://…/assets/email/*.png` in HTML (no CID). */
// function buildEmailInlineAttachmentHints(headerLogoSourceUrl: string): EmailInlineAttachmentHint[] {
//   return [
//     {
//       cid: EMAIL_CID_HEADER_LOGO,
//       sourceUrl: headerLogoSourceUrl,
//       filename: "email-header-logo",
//     },
//   ];
// }

// /** Footer always shows these three networks (PNG from /assets/email/). */
// const EMAIL_FOOTER_SOCIAL_ORDER: ReadonlyArray<{ key: VendorEmailSocialKey; title: string }> = [
//   { key: "facebook", title: "Facebook" },
//   { key: "instagram", title: "Instagram" },
//   { key: "linkedin", title: "LinkedIn" },
// ];

// /**
//  * Load marketplace profile (public) and resolve logo file id to a URL via work-drive (same idea as MarketplaceVendorProfile).
//  * Pass session `token` when `logo` is a 24-char file id so the header image works in emails.
//  */
// function cloudFileRecordToUrl(cf: { url?: string; raw?: unknown }): string | undefined {
//   if (cf.url && String(cf.url).trim()) return String(cf.url).trim();
//   const r = cf.raw as any;
//   return (
//     r?.url ||
//     r?.signedUrl ||
//     r?.presignedUrl ||
//     r?.presigned_url ||
//     r?.data?.url ||
//     r?.data?.signedUrl ||
//     r?.data?.presignedUrl ||
//     undefined
//   );
// }

// /** Optional JSON in trigger params: {"facebook":"https://...","linkedin":"..."} */
// export function parseVendorSocialLinksFromParams(
//   params: Record<string, string>
// ): VendorEmailSocialLinks | undefined {
//   const raw = params.email_social_links_json?.trim();
//   if (!raw) return undefined;
//   try {
//     const o = JSON.parse(raw) as Record<string, unknown>;
//     if (!o || typeof o !== "object") return undefined;
//     const out: VendorEmailSocialLinks = {};
//     VENDOR_EMAIL_SOCIAL_KEYS.forEach((k) => {
//       const v = o[k];
//       if (typeof v === "string" && v.trim()) out[k] = v.trim();
//     });
//     return Object.keys(out).length ? out : undefined;
//   } catch {
//     console.warn("[emailTemplate] email_social_links_json is not valid JSON; ignoring.");
//     return undefined;
//   }
// }

// export async function fetchVendorEmailBrandingByCreatorUserId(
//   createdByUserId: string,
//   token: string | null
// ): Promise<EmailBrandingOptions> {
//   const api = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
//   if (!api || !createdByUserId?.trim()) {
//     console.log("[emailTemplate] fetchVendorEmailBranding: skipped (no API URL or empty user id).");
//     return {};
//   }

//   const url = `${api}/public/marketplace-profiles/created-by/${encodeURIComponent(createdByUserId.trim())}`;
//   try {
//     console.log("[emailTemplate] fetchVendorEmailBranding GET", url);
//     const res = await fetch(url);
//     if (!res.ok) {
//       console.warn("[emailTemplate] fetchVendorEmailBranding failed:", res.status, await res.text().catch(() => ""));
//       return {};
//     }
//     const data = await res.json();
//     const profile = Array.isArray(data) ? data[0] : data;
//     if (!profile || typeof profile !== "object") {
//       console.warn("[emailTemplate] fetchVendorEmailBranding: empty or invalid profile payload.");
//       return {};
//     }

//     const sl = (profile as any).social_links;
//     const socialLinks: VendorEmailSocialLinks = {};
//     if (sl && typeof sl === "object") {
//       VENDOR_EMAIL_SOCIAL_KEYS.forEach((k) => {
//         const v = typeof sl[k] === "string" ? sl[k].trim() : "";
//         if (v) socialLinks[k] = v;
//       });
//     }

//     let headerLogoUrl: string | undefined;
//     const rawLogo = (profile as any).logo;
//     if (typeof rawLogo === "string") {
//       const L = rawLogo.trim();
//       if (/^https?:\/\//i.test(L)) {
//         headerLogoUrl = L;
//       } else if (/^[0-9a-f]{24}$/i.test(L) && token) {
//         try {
//           const cf = await getCloudFileById(L, token);
//           headerLogoUrl = cloudFileRecordToUrl(cf);
//           if (!headerLogoUrl) {
//             console.warn("[emailTemplate] Work-drive file resolved but no image URL in response for id", L);
//           }
//         } catch (e) {
//           console.warn("[emailTemplate] getCloudFileById failed for logo id", L, e);
//           headerLogoUrl = undefined;
//         }
//       } else if (L.startsWith("/")) {
//         headerLogoUrl = resolveEmailAbsoluteAssetUrl(L);
//       }
//     }

//     const vendorName =
//       typeof (profile as any).vendor_name === "string" ? String((profile as any).vendor_name).trim() : "";
//     const companyName =
//       typeof (profile as any).company_name === "string" ? String((profile as any).company_name).trim() : "";
//     const signatureDisplayName = companyName || vendorName || "";

//     const out: EmailBrandingOptions = {
//       headerLogoUrl,
//       headerLogoAlt: signatureDisplayName || vendorName || "Vendor",
//       vendorName: vendorName || undefined,
//       signatureDisplayName: signatureDisplayName || undefined,
//       socialLinks: Object.keys(socialLinks).length ? socialLinks : undefined,
//     };
//     console.log("[emailTemplate] fetchVendorEmailBranding result:", {
//       hasLogoUrl: !!out.headerLogoUrl,
//       socialKeys: out.socialLinks ? Object.keys(out.socialLinks) : [],
//       signatureDisplayName: out.signatureDisplayName || null,
//     });
//     return out;
//   } catch (e) {
//     console.warn("[emailTemplate] fetchVendorEmailBranding error:", e);
//     return {};
//   }
// }

// /** True when `src` can be used directly in HTML so clients (e.g. Gmail) load over HTTPS without CID MIME parts. */
// function isAbsoluteHttpUrl(s: string | undefined | null): boolean {
//   return /^https?:\/\//i.test(String(s || "").trim());
// }

// /**
//  * Header logo: prefer a stable **absolute https URL** in `src` when available so Gmail shows the image even if
//  * `/email/send-inline-images` still needs a working backend; otherwise fall back to `cid:` + {@link buildEmailInlineAttachmentHints}.
//  */
// function generateEmailHeader(altText = "RealEsy", imgSrcEscaped: string): string {
//   const alt = escapeHtmlAttr(altText || "RealEsy");
//   return `
//             <!-- Header logo: https URL preferred; else cid realesy_email_header_logo + inline attachment -->
//             <tr>
//               <td align="center" style="padding:32px 20px;border-bottom:1px solid #e5e7eb;">
//                 <img src="${imgSrcEscaped}" alt="${alt}" style="height:100px;width:200px;max-width:100%;display:block;margin:0 auto;object-fit:contain;border:0;outline:none;text-decoration:none;" />
//               </td>
//             </tr>`;
// }

// /** Match `Order_summary` powered-by defaults for email footers */
// const EMAIL_POWERED_BY_TEXT = "Powered by RealEsy";
// const EMAIL_POWERED_BY_PREFIX = "Powered by ";
// const EMAIL_POWERED_BY_BRAND = "RealEsy";
// const EMAIL_POWERED_BY_URL = "https://realesy.com/";

// function buildFooterSocialCells(socialLinks?: VendorEmailSocialLinks | null): string[] {
//   return EMAIL_FOOTER_SOCIAL_ORDER.map(({ key, title }) => {
//     const raw = socialLinks?.[key]?.trim();
//     const href = raw ? normalizeHref(raw) : "#";
//     const h = escapeHtmlAttr(href || "#");
//     const t = escapeHtmlAttr(title);
//     /** Public HTTPS asset URLs — Gmail loads these without relying on CID inline parts from the mail API. */
//     const src = escapeHtmlAttr(getEmailFooterPublicAssetIconUrl(key));
//     return `<td class="email-footer-social-cell" align="center" style="padding:0 10px;vertical-align:middle;text-align:center;">
//                             <a href="${h}" class="email-footer-social-icon" title="${t}" aria-label="${t}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;line-height:0;margin:0 auto;">
//                               <img src="${src}" alt="${t}" width="32" height="32" class="email-footer-social-img" style="display:block;width:32px;height:32px;max-width:32px;border:0;outline:none;text-decoration:none;" />
//                             </a>
//                           </td>`;
//   });
// }

// function buildEmailFooterPoweredByHtml(): string {
//   const url = EMAIL_POWERED_BY_URL.trim();
//   const brand = escapeHtmlAttr(EMAIL_POWERED_BY_BRAND);
//   const prefix = escapeHtmlAttr(EMAIL_POWERED_BY_PREFIX);
//   const full = escapeHtmlAttr(EMAIL_POWERED_BY_TEXT);
//   if (url) {
//     const href = escapeHtmlAttr(url);
//     return `<p class="email-footer-powered"><span class="email-footer-powered-lead">${prefix}</span><a href="${href}" class="email-footer-powered-brand" target="_blank" rel="noopener noreferrer">${brand}</a></p>`;
//   }
//   return `<p class="email-footer-powered">${full}</p>`;
// }

// /**
//  * Generate email footer — social icons use public HTTPS asset URLs; link URLs when provided else "#".
//  */
// function generateEmailFooter(footerText: string, socialLinks?: VendorEmailSocialLinks | null): string {
//   const cells = buildFooterSocialCells(socialLinks);

//   const socialRow = `<tr>
//                     <td class="email-footer-social-outer" align="center" width="100%" style="padding:18px 16px 20px;text-align:center;width:100%;">
//                       <table class="email-footer-social-table" align="center" cellpadding="0" cellspacing="0" border="0" role="presentation" style="margin:0 auto;border-collapse:collapse;text-align:center;">
//                         <tr>
//                           ${cells.join("")}
//                         </tr>
//                       </table>
//                     </td>
//                   </tr>`;

//   const poweredRow = `<tr>
//                     <td align="center" class="email-footer-powered-wrap" width="100%" style="padding-top:12px;padding-bottom:4px;text-align:center;width:100%;">
//                       ${buildEmailFooterPoweredByHtml()}
//                     </td>
//                   </tr>`;

//   return `
//             <!-- Footer -->
//             <tr>
//               <td class="email-footer">
//                 <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;width:100%;text-align:center;">
//                   ${socialRow}
//                   <tr>
//                     <td align="center" class="email-footer-text-wrap" width="100%" style="padding:8px 16px 4px;text-align:center;width:100%;">
//                       <p class="email-footer-text">
//                         ${footerText}
//                       </p>
//                     </td>
//                   </tr>
//                   ${poweredRow}
//                 </table>
//               </td>
//             </tr>`;
// }


// /**
//  * Builds { subject, html, inlineAttachments } with table-based email layout.
//  * - Header: logo via `cid:` only; source bytes described in `inlineAttachments` (vendor URL or default asset)
//  * - Footer: social icons via absolute `https://…/assets/email/*.png` (no CID; reliable in Gmail)
//  * - Footer: optional vendor social link URLs from `branding.socialLinks`
//  * - Dynamic year in footer
//  * - Body content based on trigger and license type
//  *
//  * `sendTriggerEmails` loads branding using `getAuthCookie("userId")` for the marketplace
//  * `created-by` URL (optional params override: `email_marketplace_created_by`).
//  *
//  * **Inline images:** When the header uses `cid:` (no absolute http(s) URL), `inlineAttachments` lists the logo
//  * `cid` + `sourceUrl` for the mail API to attach. If the header uses a direct `https?://` URL, `inlineAttachments`
//  * may be empty. Footer icons do not use CID.
//  */
// export function generateEmailHTML(
//   triggerName: string,
//   licenseType: string,
//   params: Record<string,string>,
//   branding?: EmailBrandingOptions
// ): { subject: string; html: string; inlineAttachments: EmailInlineAttachmentHint[] } {
//   const cfg: TriggerConfig | undefined = triggerConfig[triggerName];
//   console.log("generateEmailHTML - triggerName:", triggerName, "licenseType:", licenseType, "cfg:", cfg);
//   if (!cfg) throw new Error(`Unknown trigger "${triggerName}"`);

//   // 1) Build body HTML based on trigger-specific content structure
//   let bodyHtml: string;
  
//   // Check if bodyHtml is provided (takes priority)
//   if (cfg.bodyHtml) {
//     // Handle bodyHtml as string or Record<string,string>
//     if (typeof cfg.bodyHtml === "string") {
//       bodyHtml = interpolate(cfg.bodyHtml, params);
//     } else {
//       // It's a Record, get the one for this licenseType
//       const htmlForType = cfg.bodyHtml[licenseType];
//       if (htmlForType) {
//         bodyHtml = interpolate(htmlForType, params);
//       } else {
//         throw new Error(`No bodyHtml for "${licenseType}" under "${triggerName}"`);
//       }
//     }
//     // Convert CSS classes to inline styles for email compatibility
//     bodyHtml = convertClassesToInlineStyles(bodyHtml);
//   } else if (cfg.bodyLines) {
//     // Fallback to bodyLines if bodyHtml is not provided
//     let lines: string[];
//     if (Array.isArray(cfg.bodyLines)) {
//       lines = cfg.bodyLines;
//     } else {
//       lines = cfg.bodyLines[licenseType];
//       if (!lines) {
//         throw new Error(`No bodyLines for "${licenseType}" under "${triggerName}"`);
//       }
//     }
//     // Interpolate and wrap each line in a paragraph
//     const interpolatedLines = lines.map(line => interpolate(line, params));
//     bodyHtml = interpolatedLines.map(line => `<p style="margin:0 0 1em;color:#4b5563;font-size:16px;line-height:24px;font-family:'Roboto',Arial,sans-serif;">${line}</p>`).join("");
//   } else {
//     throw new Error(`No body content defined for trigger "${triggerName}"`);
//   }

//   // 2) optional CTA button - interpolate URL if it contains placeholders
//   // Handle CTA as object or Record for license-type-specific CTAs
//   let ctaHtml = "";
//   if (cfg.cta) {
//     let cta: { text: string; url: string };
//     if (typeof cfg.cta === "object" && !("text" in cfg.cta)) {
//       // It's a Record, get the one for this licenseType
//       const ctaForType = cfg.cta[licenseType];
//       if (ctaForType) {
//         cta = ctaForType;
//       } else {
//         // Fallback to first available CTA
//         const firstCta = Object.values(cfg.cta)[0];
//         cta = firstCta || { text: "", url: "" };
//       }
//     } else {
//       // It's a single CTA object
//       cta = cfg.cta as { text: string; url: string };
//     }
    
//     if (cta && cta.text && cta.url) {
//       const ctaUrlSource = TRIGGER_CTA_KEEP_CONFIGURED_URL.has(triggerName)
//         ? cta.url
//         : emailCtaLoginUrl();
//       const ctaUrl = interpolate(ctaUrlSource, params);
//       ctaHtml = `<div style="margin:32px 0;text-align:center;">
//          <a href="${ctaUrl}"
//             style="
//               display:inline-block;
//               padding:12px 32px;
//               background-color:#2563EB;
//               color:#ffffff;
//               font-size:16px;
//               font-weight:500;
//               text-decoration:none;
//               border-radius:8px;
//               font-family:'Roboto',Arial,sans-serif;
//             ">
//            ${cta.text}
//          </a>
//        </div>`;
//     }
//   }

//   // 3) Handle subject - can be string or Record<string,string> for license-type-specific subjects
//   // Must be resolved BEFORE using in HTML template
//   let subject: string;
//   if (typeof cfg.subject === "string") {
//     subject = interpolate(cfg.subject, params);
//   } else {
//     // It's a Record, get the one for this licenseType
//     const subjectForType = cfg.subject[licenseType];
//     if (subjectForType) {
//       subject = interpolate(subjectForType, params);
//     } else {
//       // Fallback to first available subject or default
//       const firstSubject = Object.values(cfg.subject)[0];
//       subject = firstSubject ? interpolate(firstSubject, params) : cfg.title;
//     }
//   }

//   // 4) Dynamic year and footer text
//   const year = new Date().getFullYear();
//   const footerText = `© ${year} ${cfg.footer}`;

//   // 5) Generate header and footer (optional vendor logo + social from marketplace / params)
//   const explicitLogo = params.email_header_logo_url?.trim();
//   let vendorLogoUrl = explicitLogo || branding?.headerLogoUrl?.trim();
//   if (vendorLogoUrl && !/^https?:\/\//i.test(vendorLogoUrl) && !vendorLogoUrl.startsWith("data:")) {
//     vendorLogoUrl = resolveEmailAbsoluteAssetUrl(vendorLogoUrl);
//   }
//   const useVendorLogo = !!vendorLogoUrl;
//   const defaultLogoResolved = getBundledDefaultLogoUrl();
//   const headerLogoSourceUrl = useVendorLogo && vendorLogoUrl ? vendorLogoUrl : defaultLogoResolved;
//   const headerUseDirectUrl = isAbsoluteHttpUrl(headerLogoSourceUrl);
//   const headerImgEscaped = headerUseDirectUrl
//     ? escapeHtmlAttr(headerLogoSourceUrl.trim())
//     : escapeHtmlAttr(`cid:${EMAIL_CID_HEADER_LOGO}`);
//   const emailHeader = generateEmailHeader(
//     useVendorLogo ? (branding?.headerLogoAlt || "Vendor") : "RealEsy",
//     headerImgEscaped
//   );
//   /** When the header uses a direct `https?://` src, skip CID attachments (mail API often fails to map cid → MIME for Gmail). */
//   const inlineAttachments = headerUseDirectUrl ? [] : buildEmailInlineAttachmentHints(headerLogoSourceUrl);

//   const socialFromParams = parseVendorSocialLinksFromParams(params);
//   const mergedSocialInput: VendorEmailSocialLinks = {
//     ...(branding?.socialLinks || {}),
//     ...(socialFromParams || {}),
//   };
//   const mergedSocial: VendorEmailSocialLinks | undefined = (() => {
//     const o: VendorEmailSocialLinks = {};
//     VENDOR_EMAIL_SOCIAL_KEYS.forEach((k) => {
//       const v = mergedSocialInput[k]?.trim();
//       if (v) o[k] = v;
//     });
//     return Object.keys(o).length ? o : undefined;
//   })();

//   console.log("[emailTemplate] bodyHtml length:", bodyHtml.length, "snippet:", bodyHtml.slice(0, 160).replace(/\s+/g, " "));
//   console.log(
//     "[emailTemplate] header image:",
//     useVendorLogo ? `vendor (${vendorLogoUrl?.slice(0, 80)}…)` : `default (${defaultLogoResolved.slice(0, 80)}…)`,
//     headerUseDirectUrl ? "→ direct https src (no CID attachment)" : `→ cid ${EMAIL_CID_HEADER_LOGO} + inline attachment`
//   );
//   console.log(
//     "[emailTemplate] footer social:",
//     mergedSocial ? `dynamic URLs for: ${Object.keys(mergedSocial).join(", ")}` : "default # hrefs for Facebook, Instagram, LinkedIn icons"
//   );

//   let emailFooter = generateEmailFooter(footerText, mergedSocial);
//   // Convert footer CSS classes to inline styles
//   emailFooter = convertClassesToInlineStyles(emailFooter);

//   // Signature line: marketplace company_name || vendor_name, or param override, else "-"
//   const signatureDisplayRaw =
//     params.email_signature_display_name?.trim() ||
//     branding?.signatureDisplayName?.trim() ||
//     "-";
//   const signatureDisplayEscaped = escapeHtmlText(signatureDisplayRaw);

//   // 6) Build simple, responsive HTML using tables (best for email clients)
//   // Header is ALWAYS included in the HTML template
//   const html = `
// <!DOCTYPE html>
// <html lang="en">
//   <head>
//     <meta charset="utf-8"/>
//     <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
//     <title>${subject}</title>
//     <style>
//       @media only screen and (max-width: 600px) {
//         .email-wrapper { width: 100% !important; padding: 10px !important; }
//         .email-card { width: 100% !important; max-width: 100% !important; }
//         .email-content { padding: 24px 16px !important; }
//         .email-footer {
//           padding: 28px 20px 26px !important;
//           text-align: center !important;
//           width: 100% !important;
//         }
//         .email-footer > table { margin: 0 auto !important; width: 100% !important; }
//         .email-footer-social-outer {
//           padding: 22px 12px 22px !important;
//           text-align: center !important;
//           width: 100% !important;
//         }
//         .email-footer-social-table {
//           width: auto !important;
//           max-width: 100% !important;
//           margin-left: auto !important;
//           margin-right: auto !important;
//           text-align: center !important;
//         }
//         .email-footer-social-cell { padding: 0 12px !important; text-align: center !important; }
//         .email-footer-social-img {
//           width: 36px !important;
//           height: 36px !important;
//           max-width: 36px !important;
//         }
//         .email-footer-text-wrap {
//           padding: 12px 12px 8px !important;
//           text-align: center !important;
//           width: 100% !important;
//         }
//         .email-footer-text {
//           line-height: 1.5 !important;
//           padding: 0 8px !important;
//           text-align: center !important;
//         }
//         .email-footer-powered-wrap {
//           padding-top: 14px !important;
//           padding-bottom: 8px !important;
//           text-align: center !important;
//           width: 100% !important;
//         }
//         .email-footer-powered { text-align: center !important; }
//         .access-table { font-size: 12px !important; }
//         .access-table td { padding: 6px 0 !important; }
//         .email-heading-h2 { font-size: 22px !important; line-height: 1.3 !important; }
//         .email-heading-h3 { font-size: 18px !important; line-height: 1.35 !important; display: block !important; }
//         .email-paragraph, .email-paragraph-small {
//           max-width: 100% !important;
//           word-break: break-word !important;
//           overflow-wrap: anywhere !important;
//         }
//         .email-order-details-card {
//           padding: 16px !important;
//           max-width: 100% !important;
//           box-sizing: border-box !important;
//         }
//         .email-order-details-card p {
//           max-width: 100% !important;
//           word-break: break-word !important;
//           overflow-wrap: anywhere !important;
//         }
//         .email-order-details-table { display: block !important; width: 100% !important; table-layout: auto !important; }
//         .email-order-details-table tbody { display: block !important; width: 100% !important; }
//         .email-order-details-table tr {
//           display: block !important;
//           width: 100% !important;
//           margin-bottom: 12px !important;
//           padding-bottom: 10px !important;
//           border-bottom: 1px solid #e5e7eb !important;
//           box-sizing: border-box !important;
//         }
//         .email-order-details-table tr:last-child { border-bottom: none !important; margin-bottom: 0 !important; padding-bottom: 0 !important; }
//         .email-order-details-table td {
//           display: block !important;
//           width: 100% !important;
//           max-width: 100% !important;
//           box-sizing: border-box !important;
//           border-top: none !important;
//           padding: 4px 0 !important;
//         }
//         .email-order-details-field,
//         .email-order-details-field-first {
//           width: 100% !important;
//           max-width: 100% !important;
//           color: #374151 !important;
//           font-weight: 600 !important;
//           font-size: 13px !important;
//         }
//         .email-order-details-value,
//         .email-order-details-value-first,
//         .email-order-details-value-bold,
//         .email-order-details-value-bold-first,
//         .email-order-details-value-link {
//           width: 100% !important;
//           max-width: 100% !important;
//           font-size: 15px !important;
//           word-break: break-word !important;
//           overflow-wrap: anywhere !important;
//         }
//       }
//     .email-heading-h2 {
//   margin: 0 0 16px 0;
//   color: #111827;
//   font-size: 24px;
//   font-weight: 500;
//   font-family: 'Roboto', Arial, sans-serif;
//   word-break: break-word;
//   overflow-wrap: anywhere;
//   max-width: 100%;
// }

// .email-heading-h3 {
//   margin: 0 0 16px 0;
//   color: #111827;
//   font-size: 20px;
//   font-weight: 500;
//   font-family: 'Roboto', Arial, sans-serif;
//   display: block;
// }

// .email-paragraph {
//   margin: 0 0 32px 0;
//   // color: #4b5563;
//   color: #000000;
//   font-size: 16px;
//   line-height: 24px;
//   font-family: 'Roboto', Arial, sans-serif;
//   word-break: break-word;
//   overflow-wrap: anywhere;
//   max-width: 100%;
//   font-weight: 400;
// }

// .email-paragraph-small {
//   margin: 0 0 24px 0;
//   // color: #4b5563;
//    color: #000000;
//   font-size: 16px;
//   line-height: 24px;
//   font-family: 'Roboto', Arial, sans-serif;
//   word-break: break-word;
//   overflow-wrap: anywhere;
//   max-width: 100%;
//   font-weight: 400;
// }

// /* Order Details Card */
// .email-order-details-card {
//   background-color: #f9fafb;
//   border-radius: 8px;
//   padding: 25px;
//   margin: 0 0 32px 0;
//   border: 1px solid #e5e7eb;
//   overflow: hidden;
//   box-sizing: border-box;
//   max-width: 100%;
// }

// .email-order-details-table {
//   width: 100%;
//   border-collapse: collapse;
//   table-layout: fixed;
//   max-width: 100%;
//   box-sizing: border-box;
// }

// .email-order-details-field {
//   padding: 8px 0;
//   color: #6b7280;
//   font-size: 14px;
//   vertical-align: top;
//   width: 35%;
//   word-wrap: break-word;
//   border-top: 1px solid #e5e7eb;
// }

// .email-order-details-value {
//   padding: 8px 0;
//   color: black;
//   font-size: 14px;
//   font-weight: 500;
//   vertical-align: top;
//   border-top: 1px solid #e5e7eb !important;
//   word-break: break-word;
//   overflow-wrap: break-word;
//   max-width: 0;
// }

// .email-order-details-value-first {
//   padding: 8px 0;
//   color: #111827;
//   font-size: 13px;
//   font-weight: 600;
//   vertical-align: top;
//   word-break: break-word;
//   overflow-wrap: break-word;
//   max-width: 0;
// }

// .email-order-details-value-bold {
//   padding: 8px 0;
//   color: #111827;
//   font-size: 13px;
//   font-weight: 600;
//   vertical-align: top;
//   word-break: break-word;
//   overflow-wrap: break-word;
//   max-width: 0;
//   border-top: 1px solid #e5e7eb !important;
// }

// .email-order-details-value-bold-first {
//   padding: 8px 0;
//   color: #111827;
//   font-size: 13px;
//   font-weight: 600;
//   vertical-align: top;
//   word-break: break-word;
//   overflow-wrap: break-word;
//   max-width: 0;
// }

// .email-order-details-value-link {
//   padding: 8px 0;
//   color: #2563EB;
//   font-size: 13px;
//   font-weight: 600;
//   vertical-align: top;
//   border-top: 1px solid #e5e7eb !important;
//   word-break: break-all;
//   overflow-wrap: anywhere;
//   max-width: 0;
// }

// .email-order-details-field-first {
//   padding: 8px 0;
//   color: #6b7280;
//   font-size: 13px;
//   vertical-align: top;
//   width: 35%;
//   word-wrap: break-word;
// }

// /* Line Items Card */
// .email-line-items-heading {
//   margin: 32px 0 16px 0;
//   color: #111827;
//   font-size: 20px;
//   font-weight: 500;
//   font-family: 'Roboto', Arial, sans-serif;
//   display: flex;
//   gap: 5px;
// }

// .email-line-items-container {
//   background-color: #f9fafb !important;
//   border-radius: 8px;
//   padding: 16px;
//   margin: 0 0 32px 0;
//   border: 1px solid #e5e7eb;
//   overflow: hidden;
// }

// .email-line-item-row {
//   padding: 12px 0;
//   /* border-top: 1px solid #e5e7eb; */
//   background-color: #f9fafb;
// }

// .email-line-item-row:first-child {
//   padding: 0;
//   border-top: none;
// }

// .email-line-item-table {
//   width: 100%;
//   border-collapse: collapse;
// }

// .email-line-item-product {
//   padding: 0;
//   vertical-align: top;
// }

// .email-line-item-product-name {
//   color: black;
//   font-size: 14px;
//   font-weight: 500;
//   margin-bottom: 4px;
// }

// .email-line-item-product-desc {
//   color: #6b7280;
//   font-size: 14px;
//   line-height: 18px;
// }

// .email-line-item-qty {
//   padding: 0 0 0 16px;
//   vertical-align: top;
//   text-align: right;
//   white-space: nowrap;
//   font-size: 14px;
//   font-weight: 500;
//   color:black;
// }

// .email-line-item-qty-text {
//   color: #111827;
//   font-size: 13px;
//   font-weight: 400;
// }

// /* Email Footer */
// .email-footer {
//   background-color: #f3f4f6;
//   padding: 28px 24px 24px;
//   border-top: 1px solid #e5e7eb;
//   text-align: center;
//   width: 100%;
//   box-sizing: border-box;
// }

// .email-footer-link {
//   color: #64748b !important;
//   font-size: 13px;
//   text-decoration: none;
//   padding: 0 6px;
// }

// .email-footer-separator {
//   color: #cbd5e1;
//   font-size: 13px;
// }

// .email-footer-text {
//   margin: 0;
//   color: #000000;
//   font-size: 12px;
//   line-height: 1.5;
//   text-align: center;
// }

// .email-footer-social-outer {
//   padding: 16px 16px 18px;
//   text-align: center;
//   width: 100%;
// }

// .email-footer-social-table {
//   margin-left: auto;
//   margin-right: auto;
//   border-collapse: collapse;
//   text-align: center;
// }

// .email-footer-social-cell {
//   padding: 0 10px;
//   vertical-align: middle;
//   text-align: center;
// }

// .email-footer-social-icon {
//   display: inline-block;
//   text-decoration: none;
//   line-height: 0;
//   padding: 4px;
//   vertical-align: middle;
// }

// .email-footer-social-img {
//   display: block;
//   border: 0;
//   outline: none;
// }

// .email-footer-powered {
//   margin: 0;
//   padding: 0;
//   font-size: 11px;
//   line-height: 1.5;
//   color: #94a3b8;
//   font-family: 'Roboto', Arial, Helvetica, sans-serif;
//   text-align: center;
// }

// .email-footer-powered-lead {
//   color: #94a3b8;
// }

// .email-footer-powered-brand {
//   color: #2563eb;
//   font-weight: 600;
//   text-decoration: none;
// }
//     </style>
//   </head>
//   <body style="margin:0;padding:0;background-color:#f4f4f4;font-family:Arial,sans-serif;">
//     <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f4;">
//       <tr>
//         <td align="center" class="email-wrapper" style="padding:20px 10px;">
//           <table width="600" cellpadding="0" cellspacing="0" class="email-card" style="max-width:100%;width:600px;background-color:#ffffff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.1);border: 1px solid #dedada;">
//             ${emailHeader}

//             <!-- Content -->
//             <tr>
//               <td class="email-content" style="padding:40px 20px;">
//                 ${bodyHtml}
//                 ${ctaHtml}
                
//                 <!-- Signature (vendor company_name || vendor_name from marketplace when branding loaded) -->
//                 <p style="margin:32px 0 0 0;color:#000000;font-size:16px;line-height:24px;font-family:'Roboto',Arial,sans-serif;">
//                   Thanks,<br/>
//                   <strong style="color:#111827;font-weight:500;">${signatureDisplayEscaped}</strong>
//                 </p>
//               </td>
//             </tr>

//             ${emailFooter}
//           </table>
//         </td>
//       </tr>
//     </table>
//   </body>
// </html>`;

//   return { subject, html, inlineAttachments };
// }
